import { useQuery } from "@tanstack/react-query";
import React from "react";

import { useAuth } from "@/context";
import { whatsappMetaConfigApi } from "@/pages/whatsapp-meta-config/whatsapp-meta-config.api";
import type { TWhatsappMetaConfig } from "@/pages/whatsapp-meta-config/whatsapp-meta-config.types";

const TEMPLATE_SEP = "###";

export function buildTemplateComboboxValue(
  name: string,
  language: string
): string {
  return `${name}${TEMPLATE_SEP}${language}`;
}

export function parseTemplateComboboxValue(
  value: string
): { name: string; language: string } | null {
  const idx = value.indexOf(TEMPLATE_SEP);
  if (idx <= 0) {
    return null;
  }
  return {
    name: value.slice(0, idx),
    language: value.slice(idx + TEMPLATE_SEP.length),
  };
}

export function useWhatsappMetaCascadeOptions(
  metaConfigId: string | undefined,
  organizationId: string | undefined,
  enabled: boolean
): {
  metaConfigs: TWhatsappMetaConfig[];
  metaConfigOptions: Array<{ value: string; label: string }>;
  phoneOptions: Array<{ value: string; label: string }>;
  templateOptions: Array<{ value: string; label: string }>;
  isMetaConfigsLoading: boolean;
  isPhonesLoading: boolean;
  isTemplatesLoading: boolean;
} {
  const { auth } = useAuth();
  const organizationRootId = auth.organization_root_id?.trim() || undefined;
  const tenantOrganizationId = organizationId?.trim() || undefined;

  /** Conexões cadastradas em /whatsapp-meta ficam em organization_root_id; campanhas usam clientId filho. */
  const shouldMergeRootMetaConfigs =
    Boolean(enabled) &&
    Boolean(organizationRootId) &&
    Boolean(tenantOrganizationId) &&
    organizationRootId !== tenantOrganizationId;

  const { data: tenantMetaList = [], isLoading: tenantListLoading } = useQuery({
    queryKey: ["whatsapp-meta-configs", tenantOrganizationId ?? ""],
    queryFn: () =>
      whatsappMetaConfigApi.list(tenantOrganizationId, { skipLoading: true }),
    enabled: Boolean(tenantOrganizationId) && enabled,
  });

  const { data: rootMetaList = [], isLoading: rootListLoading } = useQuery({
    queryKey: [
      "whatsapp-meta-configs",
      organizationRootId ?? "",
      "merge-with-tenant",
      tenantOrganizationId ?? "",
    ],
    queryFn: () =>
      whatsappMetaConfigApi.list(organizationRootId, { skipLoading: true }),
    enabled: shouldMergeRootMetaConfigs,
  });

  const metaConfigs = React.useMemo((): TWhatsappMetaConfig[] => {
    if (!shouldMergeRootMetaConfigs) {
      return tenantMetaList;
    }
    const byId = new Map<string, TWhatsappMetaConfig>();
    for (const c of tenantMetaList) {
      byId.set(c.id, c);
    }
    for (const c of rootMetaList) {
      if (!byId.has(c.id)) {
        byId.set(c.id, c);
      }
    }
    return Array.from(byId.values());
  }, [rootMetaList, shouldMergeRootMetaConfigs, tenantMetaList]);

  const isMetaConfigsLoading =
    (Boolean(tenantOrganizationId) && enabled && tenantListLoading) ||
    (shouldMergeRootMetaConfigs && rootListLoading);

  /** Só considerar "lista pronta" após um fetch real; com query desabilitada isLoading fica false e não pode liberar sub-queries cedo demais. */
  const listSettled =
    Boolean(tenantOrganizationId) &&
    enabled &&
    !tenantListLoading &&
    (!shouldMergeRootMetaConfigs || !rootListLoading);
  const selectedMetaRow = React.useMemo(
    () =>
      metaConfigId
        ? metaConfigs.find((c) => c.id === metaConfigId)
        : undefined,
    [metaConfigs, metaConfigId]
  );

  /** organization_id que o backend espera no header para este config_id (evita 404 raiz vs filial). */
  const apiOrganizationScope = React.useMemo(() => {
    if (selectedMetaRow) {
      return selectedMetaRow.organization_id;
    }
    if (metaConfigId && listSettled) {
      /* Config salvo na raiz (ou outro tenant) não aparece na listagem do clientId */
      return organizationRootId ?? tenantOrganizationId;
    }
    return tenantOrganizationId ?? organizationRootId;
  }, [
    listSettled,
    metaConfigId,
    tenantOrganizationId,
    organizationRootId,
    selectedMetaRow,
  ]);

  const subQueriesEnabled =
    Boolean(metaConfigId && apiOrganizationScope && listSettled) && enabled;

  const { data: phoneData, isLoading: isPhonesLoading } = useQuery({
    queryKey: [
      "whatsapp-meta-phone-numbers",
      metaConfigId ?? "",
      apiOrganizationScope ?? "",
    ],
    queryFn: () =>
      whatsappMetaConfigApi.phoneNumbers(
        metaConfigId as string,
        apiOrganizationScope
      ),
    enabled: subQueriesEnabled,
  });

  const { data: templatesData, isLoading: isTemplatesLoading } = useQuery({
    queryKey: [
      "whatsapp-meta-templates-page",
      metaConfigId ?? "",
      "100",
      apiOrganizationScope ?? "",
    ],
    queryFn: () =>
      whatsappMetaConfigApi.messageTemplates(metaConfigId as string, {
        limit: 100,
        organizationScopeId: apiOrganizationScope,
      }),
    enabled: subQueriesEnabled,
  });

  const metaConfigOptions = React.useMemo(
    () =>
      metaConfigs.map((c) => ({
        value: c.id,
        label: c.label?.trim() ? `${c.label} (${c.waba_id})` : c.waba_id,
      })),
    [metaConfigs]
  );

  const phoneOptions = React.useMemo(() => {
    const rows = phoneData?.data;
    if (!Array.isArray(rows)) {
      return [];
    }
    return rows.map((row: Record<string, unknown>) => {
      const id = row.id != null ? String(row.id) : "";
      const display =
        row.display_phone_number != null
          ? String(row.display_phone_number)
          : id;
      return { value: id, label: display || id };
    });
  }, [phoneData]);

  const templateOptions = React.useMemo(() => {
    const rows = templatesData?.data;
    if (!Array.isArray(rows)) {
      return [];
    }
    return rows
      .map((row: Record<string, unknown>) => {
        const name = row.name != null ? String(row.name) : "";
        const language = row.language != null ? String(row.language) : "";
        if (!name || !language) {
          return null;
        }
        return {
          value: buildTemplateComboboxValue(name, language),
          label: `${name} (${language})`,
        };
      })
      .filter((o): o is { value: string; label: string } => o != null);
  }, [templatesData]);

  return {
    metaConfigs,
    metaConfigOptions,
    phoneOptions,
    templateOptions,
    isMetaConfigsLoading,
    isPhonesLoading,
    isTemplatesLoading,
  };
}
